import React, { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Icon,
  Chip,
  Divider,
  Box,
  Tooltip
} from 'investira.react.components';
import { PessoaImage, MutedCopy } from '../../../components/molecules';
import { useAtivosContext } from '../../../contexts/AtivosContext';
import { FUNDO_SITUACAO } from '../../../enums';
import utils from '../../../utils';
import { ATIVO_TIPO } from '@investirapri/financa.js/lib/enums';
import { displays } from 'investira.react.lib';
import { formats } from 'investira.sdk';
import { buildComparadorFundosPath } from '../../ComparadorFundos/_shared/comparadorFundos.helpers';

const AtivoHeader = memo(props => {
  const { preciseOption = false, compare = false } = props;

  const { state, actions } = useAtivosContext();

  const {
    logo_pessoa_id,
    ticker,
    ativo,
    fundo,
    ativo_tipo_id,
    emissor,
    emissor_pessoa_id,
    logo_ok,
    mercado,
    isin,
    ativo_seuid,
    vencimento,
    serie,
    ativo_auxid
  } = state.ativo;

  const xPessoa = state?.pessoa || {};
  const xPessoaFantasia = xPessoa.pessoa_fantasia || emissor?.pessoa_fantasia;
  const xPessoaId = xPessoa.pessoa_id || emissor?.pessoa_id || emissor_pessoa_id;
  const { setor, cnpj } = state?.pessoa || emissor || {};

  const handleChange = event => {
    actions.handleShowPreciseChange(event.target.checked);
  };

  // Considera apenas situações diferentes de EM_FUNCIONAMENTO_NORMAL
  const xFundoSituacao = Object.values(FUNDO_SITUACAO)
    .filter(pItem => pItem.value !== FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value)
    .find(pItem => pItem.value === fundo?.situacao);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 1 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack>
            <PessoaImage
              logoPessoaId={logo_pessoa_id}
              alt={ativo}
              hasLogo={logo_ok}
              mercado={mercado}
              size={50}
            />
          </Stack>
          <Stack>
            <Typography variant="h5" color="textPrimary">
              {ticker || ativo_seuid || ativo}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="textPrimary">
                {utils.typo.shortname(xPessoaFantasia || ativo, 25)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {preciseOption && (
          <Stack>
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={<Switch checked={state.showPrecise} onChange={handleChange} />}
              label={
                <Typography variant="body2" color="textPrimary">
                  Valores simples
                </Typography>
              }
            />
          </Stack>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Stack gap={1} direction={'row'} flexWrap={'wrap'}>
          {ativo_tipo_id && (
            <MutedCopy value={ATIVO_TIPO[ativo_tipo_id].descricao}>
              <Chip
                label={ATIVO_TIPO[ativo_tipo_id].descricao}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )}

          {setor?.top_setor && (
            <MutedCopy value={setor.top_setor}>
              <Chip
                label={setor.top_setor}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )}

          {isin && (
            <MutedCopy value={isin}>
              <Chip label={isin} size="small" color="primary" variant="outlined" sx={{ px: 1.5 }} />
            </MutedCopy>
          )}

          {cnpj && (
            <MutedCopy value={cnpj}>
              <Chip
                label={displays.cnpj(cnpj)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )}

          {fundo?.cvm_175 && (
            <Chip
              label={'CVM 175'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ px: 1.5 }}
            />
          )}

          {fundo?.esg && (
            <Chip label={'ESG'} size="small" color="primary" variant="outlined" sx={{ px: 1.5 }} />
          )}

          {fundo?.exclusivo && (
            <Chip
              label={'Exclusivo'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ px: 1.5 }}
            />
          )}

          {fundo?.qualificado && (
            <Chip
              label={'Qualificado'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ px: 1.5 }}
            />
          )}

          {fundo?.estruturado && (
            <Chip
              label={'Estruturado'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ px: 1.5 }}
            />
          )}

          {ativo_auxid && (
            <MutedCopy value={ativo_auxid}>
              <Chip
                label={ativo_auxid}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )}

          {/* {vencimento && (
            <MutedCopy value={formats.formatDateCustom(vencimento, 'DD/MM/YYYY')}>
              <Chip
                label={`Vencimento: ${formats.formatDateCustom(vencimento, 'DD/MM/YYYY')}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )} */}

          {/* {serie && (
            <MutedCopy value={serie}>
              <Chip
                label={`Série: ${serie}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ px: 1.5 }}
              />
            </MutedCopy>
          )} */}

          {xFundoSituacao && (
            <Tooltip title="Situação do fundo">
              <span>
                <Chip
                  label={xFundoSituacao.descricao}
                  size="small"
                  color={xFundoSituacao.color}
                  variant="outlined"
                  sx={{ px: 1.5 }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          {compare && (
            <>
              <Stack>
                <Button
                  component={RouterLink}
                  color="primary"
                  variant="contained"
                  sx={{ px: 2, mr: 2 }}
                  size="small"
                  startIcon={<Icon iconName="compare" size={21} />}
                  to={buildComparadorFundosPath([state.ativo.ifeed_ativo_id])}>
                  Comparar
                </Button>
              </Stack>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          {xPessoaFantasia && xPessoaId && (
            <Tooltip title="Ir para empresa">
              <span>
                <Button
                  component={RouterLink}
                  title={xPessoa?.pessoa || ''}
                  endIcon={<Icon iconName="arrow-next" size={12} />}
                  size="small"
                  to={`/empresas/${xPessoaId}`}
                  sx={{ px: 2, whiteSpace: 'nowrap' }}>
                  {`${utils.typo.shortname(xPessoaFantasia || '', 25)}`}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

AtivoHeader.displayName = 'AtivoHeader';

export default AtivoHeader;
